import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Descriptions,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    message,
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import PermissionGuard from '../../components/business/PermissionGuard';
import RequestState from '../../components/business/RequestState';
import StatusTag, { statusLabel } from '../../components/business/StatusTag';
import { PageResult, PlanItem, ProjectV2 } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDate, formatDateTime, formatMoney } from '../../utils/v2';

const statuses = ['PENDING', 'PREPARING', 'DELIVERING', 'COMPLETED', 'PAUSED', 'CANCELLED'];
const projectTypeOptions = [
    { value: 'CORPORATE_TRAINING', label: '企业培训' },
    { value: 'WORKSHOP', label: '工作坊' },
    { value: 'CONSULTING', label: '咨询辅导' },
    { value: 'ASSESSMENT', label: '测评' },
    { value: 'COACHING', label: '教练' },
    { value: 'OTHER', label: '其他' },
];
const deliveryModeOptions = [
    { value: 'ONLINE', label: '线上' },
    { value: 'ONSITE', label: '线下' },
    { value: 'HYBRID', label: '混合' },
    { value: 'OTHER', label: '其他' },
];
const optionLabel = (options: { value: string; label: string }[], value?: string) =>
    options.find((item) => item.value === value)?.label || value;
const projectTransitions: { [key: string]: string[] } = {
    PENDING: ['PREPARING', 'DELIVERING', 'PAUSED', 'CANCELLED'],
    PREPARING: ['DELIVERING', 'COMPLETED', 'PAUSED', 'CANCELLED'],
    DELIVERING: ['COMPLETED', 'PAUSED', 'CANCELLED'],
};
const emptyPage: PageResult<ProjectV2> = { items: [], pageNo: 1, pageSize: 20, total: 0 };

const toDateTimeLocal = (value?: string) => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
        .toISOString()
        .slice(0, 16);
};

const toIsoDateTime = (value?: string) => (value ? new Date(value).toISOString() : undefined);

const ProjectV2Page = () => {
    const [createForm] = Form.useForm();
    const [transitionForm] = Form.useForm();
    const [projectForm] = Form.useForm();
    const [planForm] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [data, setData] = useState(emptyPage);
    const [filters, setFilters] = useState<any>({ pageNo: 1, pageSize: 20, sort: '-createdAt' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [detail, setDetail] = useState<ProjectV2 | null>(null);
    const [plans, setPlans] = useState<PlanItem[]>([]);
    const [payments, setPayments] = useState<any[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [createVisible, setCreateVisible] = useState(false);
    const [transitionVisible, setTransitionVisible] = useState(false);
    const [projectEditVisible, setProjectEditVisible] = useState(false);
    const [planVisible, setPlanVisible] = useState(false);
    const [editingPlan, setEditingPlan] = useState<PlanItem | null>(null);
    const [paymentVisible, setPaymentVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await v2Api.projects(filters));
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);
    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        const open = new URLSearchParams(window.location.hash.split('?')[1] || '').get('open');
        if (open) openDetail(Number(open));
    }, []);
    const openDetail = async (id: number) => {
        setDetailLoading(true);
        setDetail({ id } as ProjectV2);
        try {
            const result = await v2Api.project(id);
            setDetail(result);
            const [planResult, paymentResult] = await Promise.all([
                v2Api.planItems(id),
                v2Api.paymentNodes(id).catch(() => []),
            ]);
            setPlans(planResult);
            setPayments(paymentResult);
        } catch (e) {
            message.error(asV2Error(e).message);
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };
    const create = async () => {
        const values = await createForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.createProject({
                ...values,
                companyId: Number(values.companyId),
                legacyCustomerManagerId: Number(values.legacyCustomerManagerId),
                contractAmount: values.contractAmount || undefined,
                nextActionAt: toIsoDateTime(values.nextActionAt),
            });
            message.success('项目已创建');
            setCreateVisible(false);
            createForm.resetFields();
            load();
            openDetail(result.id);
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };
    const openProjectEditor = () => {
        if (!detail) return;
        projectForm.resetFields();
        projectForm.setFieldsValue({
            name: detail.name,
            projectTypeCode: detail.projectTypeCode,
            businessDomainCode: detail.businessDomainCode,
            projectManagerAdminId: detail.projectManagerAdminId,
            deliveryOwnerId: detail.deliveryOwnerId,
            deliveryMode: detail.deliveryMode,
            deliveryLocation: detail.deliveryLocation,
            businessTimezone: detail.businessTimezone || 'Asia/Shanghai',
            plannedSessionCount: detail.plannedSessionCount,
            actualSessionCount: detail.actualSessionCount,
            plannedParticipantCount: detail.plannedParticipantCount,
            actualParticipantCount: detail.actualParticipantCount,
            nextAction: detail.nextAction,
            nextActionAt: toDateTimeLocal(detail.nextActionAt),
            riskLevel: detail.riskLevel || 'NONE',
            riskSummary: detail.riskSummary,
        });
        setProjectEditVisible(true);
    };
    const saveProject = async () => {
        if (!detail) return;
        const values = await projectForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.patchProject(detail.id, {
                ...values,
                version: detail.version ?? 0,
                nextActionAt: toIsoDateTime(values.nextActionAt),
            });
            message.success('项目信息已更新');
            setDetail(result);
            setProjectEditVisible(false);
            load();
        } catch (e) {
            const err = asV2Error(e);
            if (err.code === 'VERSION_CONFLICT') {
                message.error('项目已被他人修改，已刷新为最新数据，请重新编辑');
                setProjectEditVisible(false);
                openDetail(detail.id);
            } else {
                message.error(err.message);
            }
        } finally {
            setSaving(false);
        }
    };
    const transition = async () => {
        if (!detail) return;
        const values = await transitionForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.transitionProject(detail.id, {
                ...values,
                version: detail.version,
                nextActionAt: values.nextActionAt
                    ? new Date(values.nextActionAt).toISOString()
                    : undefined,
            });
            message.success('项目状态已更新');
            setTransitionVisible(false);
            setDetail(result);
            load();
        } catch (e) {
            const err = asV2Error(e);
            message.error(
                err.code === 'VERSION_CONFLICT' ? '项目已被他人更新，请重新打开后提交' : err.message
            );
        } finally {
            setSaving(false);
        }
    };
    const openPlanCreate = () => {
        setEditingPlan(null);
        planForm.resetFields();
        planForm.setFieldsValue({ itemType: 'TASK', required: false, sortNo: plans.length + 1 });
        setPlanVisible(true);
    };
    const openPlanEdit = (plan: PlanItem) => {
        setEditingPlan(plan);
        planForm.resetFields();
        planForm.setFieldsValue({
            ...plan,
            required: Boolean(plan.required ?? plan.requiredFlag),
            plannedStartAt: toDateTimeLocal(plan.plannedStartAt),
            plannedEndAt: toDateTimeLocal(plan.plannedEndAt),
            actualStartAt: toDateTimeLocal(plan.actualStartAt),
            actualEndAt: toDateTimeLocal(plan.actualEndAt),
            adjustmentReason: undefined,
        });
        setPlanVisible(true);
    };
    const closePlanEditor = () => {
        setPlanVisible(false);
        setEditingPlan(null);
        planForm.resetFields();
    };
    const savePlan = async () => {
        if (!detail) return;
        const values = await planForm.validateFields();
        const dateValues = {
            plannedStartAt: toIsoDateTime(values.plannedStartAt),
            plannedEndAt: toIsoDateTime(values.plannedEndAt),
            actualStartAt: toIsoDateTime(values.actualStartAt),
            actualEndAt: toIsoDateTime(values.actualEndAt),
        };
        setSaving(true);
        try {
            if (editingPlan) {
                const itemVersion = editingPlan.itemVersion ?? editingPlan.version;
                if (itemVersion === undefined) throw new Error('计划项版本缺失，请刷新后重试');
                await v2Api.patchPlanItem(detail.id, editingPlan.id, {
                    ...values,
                    ...dateValues,
                    planRevision: detail.planRevision ?? 0,
                    itemVersion,
                });
                message.success('计划项已更新');
            } else {
                await v2Api.addPlanItem(detail.id, {
                    ...values,
                    ...dateValues,
                    planRevision: detail.planRevision ?? 0,
                    predecessorItemIds: [],
                });
                message.success('计划项已添加');
            }
            closePlanEditor();
            openDetail(detail.id);
        } catch (e) {
            const err = asV2Error(e);
            if (err.code === 'VERSION_CONFLICT') {
                message.error('项目计划已被他人修改，已刷新为最新数据，请重新编辑');
                closePlanEditor();
                openDetail(detail.id);
            } else {
                message.error(err.message);
            }
        } finally {
            setSaving(false);
        }
    };
    const addPayment = async () => {
        if (!detail) return;
        const values = await paymentForm.validateFields();
        setSaving(true);
        try {
            await v2Api.addPaymentNode(detail.id, values);
            message.success('回款节点已添加');
            setPaymentVisible(false);
            paymentForm.resetFields();
            openDetail(detail.id);
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };
    const projectStatus = (project: ProjectV2) => project.projectStatusCode || project.status;
    const allowedProjectStatuses = (project: ProjectV2) => {
        const current = projectStatus(project);
        if (current === 'PAUSED') {
            return [project.prePauseStatus, 'CANCELLED'].filter(Boolean) as string[];
        }
        return projectTransitions[current] || [];
    };
    const completed = plans.filter((item) => ['COMPLETED', 'NOT_APPLICABLE'].includes(item.status))
        .length;
    const columns = [
        {
            title: '项目 / 企业',
            key: 'name',
            width: 240,
            render: (_: any, row: ProjectV2) => (
                <Button type="link" className="v2-link-strong" onClick={() => openDetail(row.id)}>
                    {row.name}
                    <span className="v2-subline">{row.companyName}</span>
                </Button>
            ),
        },
        {
            title: '状态',
            key: 'status',
            width: 110,
            render: (_: any, row: ProjectV2) => <StatusTag value={projectStatus(row)} />,
        },
        {
            title: '类型 / 交付方式',
            key: 'type',
            width: 160,
            render: (_: any, row: ProjectV2) => (
                <div>
                    {optionLabel(projectTypeOptions, row.projectTypeCode) || '待完善'}
                    <div className="v2-muted">
                        {optionLabel(deliveryModeOptions, row.deliveryMode) || '方式待完善'}
                    </div>
                </div>
            ),
        },
        {
            title: '负责人',
            dataIndex: 'deliveryOwnerName',
            width: 110,
            render: (v: string, row: ProjectV2) =>
                v || row.deliveryOwnerId || row.projectManagerAdminId || '待分配',
        },
        {
            title: '计划进度',
            key: 'progress',
            width: 180,
            render: () => <span className="v2-muted">打开详情查看计划进度</span>,
        },
        {
            title: '下一行动',
            key: 'next',
            width: 230,
            render: (_: any, row: ProjectV2) => (
                <div>
                    {row.nextAction || <span className="v2-danger">缺少下一步</span>}
                    <div className="v2-muted">{formatDateTime(row.nextActionAt)}</div>
                </div>
            ),
        },
        {
            title: '风险',
            dataIndex: 'riskLevel',
            width: 90,
            render: (v: string) => <StatusTag value={v} />,
        },
    ];
    const planColumns = [
        { title: '#', dataIndex: 'sortNo', width: 52 },
        {
            title: '计划项',
            key: 'name',
            render: (_: any, row: PlanItem) => (
                <Button
                    type="link"
                    className="v2-link-strong"
                    style={{ height: 'auto', padding: 0, textAlign: 'left' }}
                    onClick={(event) => {
                        event.stopPropagation();
                        openPlanEdit(row);
                    }}
                >
                    <span>
                        <strong>{row.name}</strong>
                        <div className="v2-muted">
                            {row.itemType}
                            {(row.required || row.requiredFlag) && ' · 项目完成前必做'}
                        </div>
                    </span>
                </Button>
            ),
        },
        {
            title: '计划时间',
            key: 'time',
            width: 210,
            render: (_: any, row: PlanItem) =>
                `${formatDateTime(row.plannedStartAt)} → ${formatDateTime(row.plannedEndAt)}`,
        },
        { title: '负责人', dataIndex: 'ownerId', width: 90, render: (v: number) => v || '待分配' },
        {
            title: '状态',
            dataIndex: 'status',
            width: 110,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '操作',
            key: 'action',
            width: 80,
            render: (_: any, row: PlanItem) => (
                <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={(event) => {
                        event.stopPropagation();
                        openPlanEdit(row);
                    }}
                >
                    编辑
                </Button>
            ),
        },
    ];
    const paymentColumns = [
        {
            title: '节点',
            key: 'node',
            render: (_: any, row: any) => (
                <div>
                    <strong>{row.name}</strong>
                    <div className="v2-muted">{row.nodeType}</div>
                </div>
            ),
        },
        { title: '计划日期', dataIndex: 'plannedDate', width: 120, render: formatDate },
        {
            title: '计划金额',
            key: 'amount',
            width: 150,
            align: 'right' as const,
            render: (_: any, row: any) => formatMoney(row.plannedAmount, row.currency),
        },
        {
            title: '已分配到账',
            key: 'allocated',
            width: 150,
            align: 'right' as const,
            render: (_: any, row: any) => formatMoney(row.allocatedAmount, row.currency),
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (v: string) => <StatusTag value={v} />,
        },
    ];
    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="项目 · 交付与现金节点"
                title="项目"
                description="项目主状态与执行计划分层维护；未开票、未回款或未结算不会阻止交付完成。"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load}>
                            刷新
                        </Button>
                        <PermissionGuard anyOf={['PROJECT_V2_EDIT']}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateVisible(true)}
                            >
                                新建项目
                            </Button>
                        </PermissionGuard>
                    </Space>
                }
            />
            <Card className="v2-filter-card">
                <Space className="v2-wrap-space">
                    <Select
                        allowClear
                        placeholder="项目状态"
                        style={{ width: 160 }}
                        onChange={(status) => setFilters({ ...filters, status, pageNo: 1 })}
                    >
                        {statuses.map((status) => (
                            <Select.Option key={status} value={status}>
                                {statusLabel(status)}
                            </Select.Option>
                        ))}
                    </Select>
                    <InputNumber
                        min={1}
                        placeholder="企业 ID"
                        onChange={(companyId) => setFilters({ ...filters, companyId, pageNo: 1 })}
                    />
                    <InputNumber
                        min={1}
                        placeholder="负责人 ID"
                        onChange={(ownerId) => setFilters({ ...filters, ownerId, pageNo: 1 })}
                    />
                </Space>
            </Card>
            <Card className="v2-panel" bordered={false}>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!data.items.length}
                    onRetry={load}
                >
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data.items}
                        scroll={{ x: 1100 }}
                        pagination={{
                            current: data.pageNo,
                            pageSize: data.pageSize,
                            total: data.total,
                            showSizeChanger: true,
                            onChange: (pageNo, pageSize) =>
                                setFilters({ ...filters, pageNo, pageSize }),
                        }}
                    />
                </RequestState>
            </Card>
            <Drawer
                width={920}
                title={
                    <div className="v2-drawer-title">
                        <span>{detail && detail.name}</span>
                        <Space>
                            {detail && (
                                <Button icon={<EditOutlined />} onClick={openProjectEditor}>
                                    编辑项目信息
                                </Button>
                            )}
                            {detail &&
                                !['COMPLETED', 'CANCELLED'].includes(projectStatus(detail)) && (
                                    <PermissionGuard anyOf={['PROJECT_STATUS_MANAGE']}>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                transitionForm.resetFields();
                                                setTransitionVisible(true);
                                            }}
                                        >
                                            状态操作
                                        </Button>
                                    </PermissionGuard>
                                )}
                        </Space>
                    </div>
                }
                visible={!!detail}
                onClose={() => setDetail(null)}
                destroyOnClose
            >
                <RequestState
                    loading={detailLoading}
                    empty={false}
                    onRetry={() => detail && openDetail(detail.id)}
                >
                    {detail && (
                        <Tabs defaultActiveKey="overview">
                            <Tabs.TabPane tab="概览" key="overview">
                                <div className="v2-detail-hero">
                                    <Space>
                                        <StatusTag value={projectStatus(detail)} />
                                        <StatusTag value={detail.riskLevel} />
                                    </Space>
                                    <h3>{detail.nextAction || '缺少下一步行动'}</h3>
                                    <p>{detail.riskSummary || '暂无风险说明'}</p>
                                </div>
                                <Descriptions bordered size="small" column={2}>
                                    <Descriptions.Item label="企业">
                                        {detail.companyName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="来源商机">
                                        {detail.sourceOpportunityId || '直接创建'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="项目类型">
                                        {optionLabel(projectTypeOptions, detail.projectTypeCode) ||
                                            '待完善'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="业务领域">
                                        {detail.businessDomainCode || '待完善'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="交付方式">
                                        {optionLabel(deliveryModeOptions, detail.deliveryMode) || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="交付地点">
                                        {detail.deliveryLocation || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="完成总结" span={2}>
                                        {detail.completionSummary || '尚未完成'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Tabs.TabPane>
                            <Tabs.TabPane tab={`执行计划 ${plans.length}`} key="plan">
                                <div className="v2-tab-toolbar">
                                    <div>
                                        <strong>计划修订号 v{detail.planRevision || 0}</strong>
                                        <span className="v2-muted">
                                            {' '}
                                            · 完成 {completed}/{plans.length}
                                        </span>
                                    </div>
                                    <PermissionGuard anyOf={['PROJECT_V2_EDIT']}>
                                        <Button
                                            icon={<PlusOutlined />}
                                            onClick={openPlanCreate}
                                        >
                                            添加计划项
                                        </Button>
                                    </PermissionGuard>
                                </div>
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    columns={planColumns}
                                    dataSource={plans}
                                    scroll={{ x: 840 }}
                                    onRow={(row) => ({
                                        onClick: () => openPlanEdit(row),
                                        style: { cursor: 'pointer' },
                                        title: '点击编辑计划项',
                                    })}
                                />
                            </Tabs.TabPane>
                            <Tabs.TabPane tab="客户联系人" key="contacts">
                                {Array.isArray(detail.contacts) &&
                                    detail.contacts.map((item: any) => (
                                        <div className="v2-related-row" key={item.id}>
                                            <strong>{item.personName}</strong>
                                            <span>
                                                {[item.department, item.positionTitle]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </span>
                                            {item.primary && <Tag color="blue">主要联系人</Tag>}
                                        </div>
                                    ))}
                            </Tabs.TabPane>
                            <Tabs.TabPane tab={`开票与回款 ${payments.length}`} key="finance">
                                <div className="v2-tab-toolbar">
                                    <Alert
                                        type="info"
                                        showIcon
                                        message="金额字段由服务端按财务权限返回或隐藏"
                                    />
                                    <PermissionGuard anyOf={['FINANCE_RECORD_CONFIRM']}>
                                        <Button
                                            icon={<PlusOutlined />}
                                            onClick={() => setPaymentVisible(true)}
                                        >
                                            添加回款节点
                                        </Button>
                                    </PermissionGuard>
                                </div>
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    columns={paymentColumns}
                                    dataSource={payments}
                                />
                            </Tabs.TabPane>
                            <Tabs.TabPane tab="文件与统一导出" key="files">
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="项目计划导出接口尚未实现"
                                    description="当前不展示虚假的导出按钮；服务端完成异步导出和文件权限后再接入。"
                                />
                            </Tabs.TabPane>
                            <Tabs.TabPane tab="状态与操作历史" key="history">
                                <Alert
                                    type="info"
                                    showIcon
                                    message="状态历史已落库，独立查询接口待补充"
                                />
                            </Tabs.TabPane>
                        </Tabs>
                    )}
                </RequestState>
            </Drawer>
            <Modal
                width={660}
                title="新建项目"
                visible={createVisible}
                onOk={create}
                confirmLoading={saving}
                onCancel={() => setCreateVisible(false)}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    initialValues={{ currency: 'CNY', businessTimezone: 'Asia/Shanghai' }}
                >
                    <Form.Item label="项目名称" name="name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Space style={{ display: 'flex' }}>
                        <Form.Item label="企业 ID" name="companyId" rules={[{ required: true }]}>
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item
                            label="兼容客户经理 ID"
                            name="legacyCustomerManagerId"
                            rules={[{ required: true }]}
                        >
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="交付负责人 ID" name="deliveryOwnerId">
                            <InputNumber min={1} />
                        </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex' }}>
                        <Form.Item
                            label="项目类型"
                            name="projectTypeCode"
                            rules={[{ required: true, message: '请选择项目类型' }]}
                        >
                            <Select style={{ width: 150 }} options={projectTypeOptions} />
                        </Form.Item>
                        <Form.Item label="合同金额" name="contractAmount">
                            <Input />
                        </Form.Item>
                        <Form.Item label="币种" name="currency">
                            <Select style={{ width: 90 }}>
                                <Select.Option value="CNY">CNY</Select.Option>
                                <Select.Option value="USD">USD</Select.Option>
                            </Select>
                        </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex' }}>
                        <Form.Item label="交付方式" name="deliveryMode">
                            <Select
                                allowClear
                                placeholder="请选择"
                                style={{ width: 150 }}
                                options={deliveryModeOptions}
                            />
                        </Form.Item>
                        <Form.Item label="交付地点 / 链接" name="deliveryLocation">
                            <Input style={{ width: 280 }} />
                        </Form.Item>
                    </Space>
                    <Form.Item label="下一步行动" name="nextAction">
                        <Input />
                    </Form.Item>
                    <Form.Item label="行动时间" name="nextActionAt">
                        <Input type="datetime-local" />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                width={760}
                title="编辑项目信息"
                visible={projectEditVisible}
                zIndex={1200}
                onOk={saveProject}
                confirmLoading={saving}
                onCancel={() => setProjectEditVisible(false)}
                okText="保存修改"
                forceRender
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <Form form={projectForm} layout="vertical">
                    <Form.Item
                        label="项目名称"
                        name="name"
                        rules={[{ required: true, whitespace: true, message: '请输入项目名称' }]}
                    >
                        <Input maxLength={200} />
                    </Form.Item>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item
                            label="项目类型"
                            name="projectTypeCode"
                            rules={[{ required: true, message: '请选择项目类型' }]}
                        >
                            <Select style={{ width: 180 }} options={projectTypeOptions} />
                        </Form.Item>
                        <Form.Item label="业务领域" name="businessDomainCode">
                            <Input style={{ width: 180 }} maxLength={64} />
                        </Form.Item>
                        <Form.Item label="交付方式" name="deliveryMode">
                            <Select
                                allowClear
                                style={{ width: 160 }}
                                options={deliveryModeOptions}
                            />
                        </Form.Item>
                    </Space>
                    <Form.Item label="交付地点 / 线上链接" name="deliveryLocation">
                        <Input maxLength={300} />
                    </Form.Item>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item label="项目负责人 ID" name="projectManagerAdminId">
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="交付负责人 ID" name="deliveryOwnerId">
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="业务时区" name="businessTimezone">
                            <Input style={{ width: 180 }} maxLength={50} />
                        </Form.Item>
                    </Space>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item label="计划场次" name="plannedSessionCount">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item label="实际场次" name="actualSessionCount">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item label="计划参与人数" name="plannedParticipantCount">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item label="实际参与人数" name="actualParticipantCount">
                            <InputNumber min={0} />
                        </Form.Item>
                    </Space>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item label="风险级别" name="riskLevel">
                            <Select style={{ width: 150 }}>
                                <Select.Option value="NONE">无风险</Select.Option>
                                <Select.Option value="LOW">低</Select.Option>
                                <Select.Option value="MEDIUM">中</Select.Option>
                                <Select.Option value="HIGH">高</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="下一步行动时间" name="nextActionAt">
                            <Input type="datetime-local" style={{ width: 220 }} />
                        </Form.Item>
                    </Space>
                    <Form.Item label="下一步行动" name="nextAction">
                        <Input maxLength={500} />
                    </Form.Item>
                    <Form.Item label="风险说明" name="riskSummary">
                        <Input.TextArea rows={3} maxLength={1000} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="项目状态操作"
                visible={transitionVisible}
                zIndex={1200}
                onOk={transition}
                confirmLoading={saving}
                onCancel={() => setTransitionVisible(false)}
            >
                <Form form={transitionForm} layout="vertical">
                    <Form.Item label="目标状态" name="toStatus" rules={[{ required: true }]}>
                        <Select>
                            {(detail ? allowedProjectStatuses(detail) : []).map((status) => (
                                <Select.Option key={status} value={status}>
                                    {statusLabel(status)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="原因" name="reason" rules={[{ required: true }]}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) => prev.toStatus !== next.toStatus}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('toStatus') === 'COMPLETED' ? (
                                <Form.Item
                                    label="项目完成总结"
                                    name="completionSummary"
                                    rules={[{ required: true }]}
                                >
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>
                    <Form.Item label="下一步行动" name="nextAction">
                        <Input />
                    </Form.Item>
                    <Form.Item label="行动时间" name="nextActionAt">
                        <Input type="datetime-local" />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                width={760}
                title={editingPlan ? `编辑计划项：${editingPlan.name}` : '添加项目计划项'}
                visible={planVisible}
                zIndex={1200}
                onOk={savePlan}
                confirmLoading={saving}
                onCancel={closePlanEditor}
                okText={editingPlan ? '保存修改' : '添加'}
                forceRender
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <Form form={planForm} layout="vertical">
                    <Form.Item label="计划项名称" name="name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item label="顺序" name="sortNo" rules={[{ required: true }]}>
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="类型" name="itemType" rules={[{ required: true }]}>
                            <Select style={{ width: 160 }}>
                                <Select.Option value="PHASE">实施阶段</Select.Option>
                                <Select.Option value="TASK">工作任务</Select.Option>
                                <Select.Option value="DELIVERY_SESSION">交付场次</Select.Option>
                                <Select.Option value="REVIEW">复盘事项</Select.Option>
                                <Select.Option value="FINANCE">财务事项</Select.Option>
                                <Select.Option value="SETTLEMENT">结算事项</Select.Option>
                                <Select.Option value="OTHER">其他</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="required" valuePropName="checked">
                            <Checkbox>项目完成前必做</Checkbox>
                        </Form.Item>
                    </Space>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item label="计划开始" name="plannedStartAt">
                            <Input type="datetime-local" />
                        </Form.Item>
                        <Form.Item label="计划完成" name="plannedEndAt">
                            <Input type="datetime-local" />
                        </Form.Item>
                    </Space>
                    <Form.Item label="实施内容" name="implementationContent">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="预期产出" name="expectedOutput">
                        <Input />
                    </Form.Item>
                    <Form.Item label="完成标准" name="completionCriteria">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item
                            label="负责人类型"
                            name="ownerType"
                            dependencies={['ownerId']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        return getFieldValue('ownerId') && !value
                                            ? Promise.reject(new Error('选择负责人时必须指定负责人类型'))
                                            : Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <Select allowClear style={{ width: 180 }}>
                                <Select.Option value="ADMIN">内部用户</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="负责人 ID"
                            name="ownerId"
                            dependencies={['ownerType']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        return getFieldValue('ownerType') && !value
                                            ? Promise.reject(new Error('指定负责人类型时必须填写负责人 ID'))
                                            : Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="交付方式" name="deliveryMode">
                            <Select
                                allowClear
                                style={{ width: 160 }}
                                options={deliveryModeOptions}
                            />
                        </Form.Item>
                    </Space>
                    <Form.Item label="交付地点" name="deliveryLocation">
                        <Input />
                    </Form.Item>
                    <Form.Item label="客户配合事项" name="customerCooperation">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    {editingPlan && (
                        <>
                            <Space align="start" style={{ display: 'flex' }}>
                                <Form.Item label="状态" name="status" rules={[{ required: true }]}>
                                    <Select style={{ width: 180 }}>
                                        {[
                                            'PENDING',
                                            'READY',
                                            'IN_PROGRESS',
                                            'COMPLETED',
                                            'NOT_APPLICABLE',
                                            'CANCELLED',
                                        ].map((status) => (
                                            <Select.Option key={status} value={status}>
                                                {statusLabel(status)}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="实际开始" name="actualStartAt">
                                    <Input type="datetime-local" />
                                </Form.Item>
                                <Form.Item label="实际完成" name="actualEndAt">
                                    <Input type="datetime-local" />
                                </Form.Item>
                            </Space>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, next) => prev.status !== next.status}
                            >
                                {({ getFieldValue }) => {
                                    const status = getFieldValue('status');
                                    if (status === 'NOT_APPLICABLE') {
                                        return (
                                            <Form.Item
                                                label="不适用原因"
                                                name="notApplicableReason"
                                                rules={[{ required: true }]}
                                            >
                                                <Input.TextArea rows={2} />
                                            </Form.Item>
                                        );
                                    }
                                    if (status === 'CANCELLED') {
                                        return (
                                            <Form.Item
                                                label="取消原因"
                                                name="cancelReason"
                                                rules={[{ required: true }]}
                                            >
                                                <Input.TextArea rows={2} />
                                            </Form.Item>
                                        );
                                    }
                                    return null;
                                }}
                            </Form.Item>
                            <Form.Item label="实际结果" name="actualResult">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                            <Form.Item
                                label="本次调整原因"
                                name="adjustmentReason"
                                rules={[{ required: true, message: '请填写本次调整原因' }]}
                            >
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
            <Modal
                title="添加回款计划节点"
                visible={paymentVisible}
                zIndex={1200}
                onOk={addPayment}
                confirmLoading={saving}
                onCancel={() => setPaymentVisible(false)}
            >
                <Form
                    form={paymentForm}
                    layout="vertical"
                    initialValues={{
                        nodeNo: payments.length + 1,
                        nodeType: 'MILESTONE',
                        currency: 'CNY',
                    }}
                >
                    <Form.Item label="节点名称" name="name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Space>
                        <Form.Item label="序号" name="nodeNo" rules={[{ required: true }]}>
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="节点类型" name="nodeType" rules={[{ required: true }]}>
                            <Select style={{ width: 140 }}>
                                <Select.Option value="ADVANCE">首款</Select.Option>
                                <Select.Option value="MILESTONE">阶段款</Select.Option>
                                <Select.Option value="FINAL">尾款</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="币种" name="currency" rules={[{ required: true }]}>
                            <Select style={{ width: 90 }}>
                                <Select.Option value="CNY">CNY</Select.Option>
                                <Select.Option value="USD">USD</Select.Option>
                            </Select>
                        </Form.Item>
                    </Space>
                    <Form.Item label="计划金额" name="plannedAmount">
                        <Input />
                    </Form.Item>
                    <Form.Item label="计划日期" name="plannedDate">
                        <Input type="date" />
                    </Form.Item>
                    <Form.Item label="触发条件" name="triggerCondition">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
export default ProjectV2Page;
